<template>
  <div class="cb-container">
    <!-- Enhanced Header with Glass Effect -->
    <v-toolbar flat class="cb-toolbar cb-glass-effect">
      <div class="cb-toolbar-content">
        <div class="cb-title-section">
          <div class="cb-avatar-container">
            <div ref="lottieHeader" class="cb-lottie-header-animation" />
          </div>
          <div class="cb-title-info">
            <span class="cb-title-text">{{ $t('chat.chatbot.title') }}</span>
            <span class="cb-subtitle-text">{{ $t('chat.chatbot.subtitle') }}</span>
          </div>
        </div>

        <div class="cb-action-buttons">
          <!-- Status Indicator -->
          <div class="cb-status-indicator" :class="{ 'active': !isLoading }">
            <div class="cb-status-dot"></div>
            <span class="cb-status-text">{{ isLoading ? $t('chat.chatbot.status.thinking') : $t('chat.chatbot.status.online') }}</span>
          </div>

          <!-- Clear Button -->
          <v-btn icon @click="clearChat" :aria-label="$t('chat.chatbot.actions.clear-chat')" class="cb-action-btn cb-clear-btn" size="small">
            <v-icon class="cb-btn-icon">mdi-delete-outline</v-icon>
          </v-btn>

          <!-- Close Button -->
          <v-btn icon @click="$emit('close')" :aria-label="$t('chat.chatbot.actions.close-chat')" class="cb-action-btn cb-close-btn" size="small">
            <v-icon class="cb-btn-icon">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </v-toolbar>

    <!-- Enhanced Messages Container -->
    <div class="cb-messages-container" ref="messagesContainer">
      <div class="cb-messages-wrapper">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="cb-welcome-message">
          <div class="cb-welcome-avatar">
            <img :src="agent" alt="PIA" class="cb-welcome-agent-image" />
          </div>
          <h3 class="cb-welcome-title">{{ $t('chat.chatbot.welcome.title') }}</h3>
          <p class="cb-welcome-subtitle" v-html="$t('chat.chatbot.welcome.subtitle').replace(/\n/g, '<br>')"></p>
        </div>

        <!-- Chat Messages -->
        <div v-for="(message, index) in messages" :key="index" :class="['cb-message-wrapper', message.type]" :style="{ animationDelay: `${index * 0.1}s` }">
          <div v-if="message.type === 'user'" class="cb-user-message">
            <div class="cb-message-content">
              <div class="cb-message-text">{{ message.text }}</div>
              <div class="cb-message-time">{{ formatTime(message.timestamp) }}</div>
            </div>
            <div class="cb-user-avatar">
              <v-icon class="cb-avatar-icon">mdi-account</v-icon>
            </div>
          </div>

          <div v-else class="cb-bot-message">
            <div class="cb-bot-avatar">
              <img :src="agent" :alt="$t('chat.chatbot.messages.assistant')" class="cb-bot-agent-image" />
            </div>
            <div class="cb-message-content">
              <div class="cb-message-text" v-html="message.text"></div>
              <div class="cb-message-footer">
                <div class="cb-message-time">{{ formatTime(message.timestamp) }}</div>
                <div v-if="message.tokens" class="cb-token-count">
                  <v-icon class="cb-token-icon" size="12">mdi-counter</v-icon>
                  <span>{{ message.tokens.input + message.tokens.output }} {{ $t('chat.chatbot.messages.tokens') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Loading Indicator -->
        <div v-show="isLoading" class="cb-loading-wrapper">
          <div class="cb-bot-avatar">
            <img :src="agent" :alt="$t('chat.chatbot.messages.assistant')" class="cb-bot-agent-image" />
          </div>
          <div class="cb-loading-message">
            <div ref="lottieSpinner" class="cb-lottie-spinner"></div>
            <div class="cb-typing-indicator">
              <span class="cb-typing-text">{{ $t('chat.chatbot.loading.typing') }}</span>
              <div class="cb-typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Input Container -->
    <div class="cb-input-container cb-glass-effect">
      <div class="cb-input-wrapper">
        <div class="cb-input-field-container">
          <input
            ref="inputRef"
            v-model="animatedInput"
            type="text"
            :placeholder="$t('chat.chatbot.input.placeholder')"
            :disabled="isLoading"
            @keyup.enter="sendMessage"
            @focus="inputFocused = true"
            @blur="inputFocused = false"
            class="cb-input-field"
          />
          <div class="cb-input-border" :class="{ 'focused': inputFocused }"></div>
        </div>

        <button
          class="cb-send-btn"
          @click="sendMessage"
          :disabled="isLoading || !animatedInput.trim()"
          :class="{ 'has-text': animatedInput.trim() }"
          :aria-label="$t('chat.chatbot.actions.send-message')"
        >
          <v-icon class="cb-send-icon">
            {{ isLoading ? 'mdi-loading' : 'mdi-send' }}
          </v-icon>
        </button>
      </div>
    </div>

    <!-- Enhanced Confirmation Snackbar -->
    <v-snackbar v-model="showClearConfirm" location="center" timeout="5000" class="cb-custom-snackbar" rounded="xl">
      <div class="cb-snackbar-content">
        <v-icon class="cb-snackbar-icon">mdi-delete-alert</v-icon>
        <div class="cb-snackbar-text">
          <span>{{ $t('chat.chatbot.confirmation.clear-title') }}</span>
          <small>{{ $t('chat.chatbot.confirmation.clear-subtitle') }}</small>
        </div>
        <div class="cb-snackbar-actions">
          <v-btn color="#ff5252" variant="flat" size="small" class="cb-confirm-btn" @click="confirmClearChat">
            {{ $t('chat.chatbot.confirmation.clear-confirm') }}
          </v-btn>
          <v-btn variant="text" size="small" class="cb-cancel-btn" @click="showClearConfirm = false">
            {{ $t('chat.chatbot.confirmation.clear-cancel') }}
          </v-btn>
        </div>
      </div>
    </v-snackbar>
  </div>
</template>

<script setup>
  import { ref, nextTick, onMounted, onUnmounted, defineProps } from "vue";
  import { Capacitor } from "@capacitor/core";
  import { marked } from "marked";
  import lottie from "lottie-web";
  // Animations
  import lottieLoader from "@/ai/aiAnimations/lottieLoader.json";
  import lottieHeaderJson from "@/ai/aiAnimations/lottie_header.json";
  // Services
  import { sendToAssistant } from "@/ai/aiServices/assistantService.js";
  import { loadMessages, saveMessages } from "@/ai/aiServices/chatStorage.js";
  import agent from "@/ai/aiAnimations/agent.png";

  const props = defineProps({
      initialMessage: String,
  });

  const messages = ref([]);
  const animatedInput = ref('');
  const isLoading = ref(false);
  const inputFocused = ref(false);
  const messagesContainer = ref(null);
  const threadId = ref(null);
  const lottieSpinner = ref(null);
  const showClearConfirm = ref(false);
  let lottieInstance = null;
  const lottieHeader = ref(null);
  let lottieHeaderInstance = null;

  // Format time helper
  function formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
      });
  }

  function scrollToBottom() {
      nextTick(() => {
          if (messagesContainer.value) {
              const container = messagesContainer.value;
              container.scrollTo({
                  top: container.scrollHeight,
                  behavior: 'smooth'
              });
          }
      });
  }

  async function loadLottieSpinner() {
      await nextTick();
      if (lottieSpinner.value) {
          if (lottieInstance) lottieInstance.destroy();
          lottieInstance = lottie.loadAnimation({
              container: lottieSpinner.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: lottieLoader,
          });
      }
  }

  async function sendMessage() {
      const text = animatedInput.value.trim();
      if (!text || isLoading.value) return;

      const userMessage = {
          type: 'user',
          text,
          timestamp: Date.now()
      };

      messages.value.push(userMessage);

      const plain = messages.value.map((m) => ({
          type: m.type,
          text: m.text,
          timestamp: m.timestamp
      }));
      await saveMessages(plain);

      animatedInput.value = '';
      isLoading.value = true;
      await loadLottieSpinner();
      scrollToBottom();

      try {
          const { reply, threadId: returnedThreadId, tokenUsage } = await sendToAssistant(text, threadId.value);
          threadId.value = returnedThreadId;
          const htmlReply = marked.parse(reply);

          const botMessage = {
              type: 'bot',
              text: htmlReply,
              timestamp: Date.now(),
              tokens: {
                  input: tokenUsage?.inputTokens ?? 0,
                  output: tokenUsage?.outputTokens ?? 0,
              },
          };

          messages.value.push(botMessage);

          const plainUpdated = messages.value.map((m) => ({
              type: m.type,
              text: m.text,
              timestamp: m.timestamp,
              tokens: m.tokens
          }));
          await saveMessages(plainUpdated);
      } catch (error) {
          messages.value.push({
              type: 'bot',
              text: `❌ Sorry, something went wrong: ${error.message || error}`,
              timestamp: Date.now()
          });
      } finally {
          isLoading.value = false;
          lottieInstance?.destroy();
          lottieInstance = null;
          scrollToBottom();
      }
  }

  function clearChat() {
      showClearConfirm.value = true;
  }

  async function confirmClearChat() {
      showClearConfirm.value = false;
      isLoading.value = true;
      await loadLottieSpinner();
      scrollToBottom();

      setTimeout(async () => {
          messages.value = [];
          animatedInput.value = '';
          threadId.value = null;
          await saveMessages([]);
          isLoading.value = false;

          if (lottieInstance) {
              lottieInstance.destroy();
              lottieInstance = null;
          }
      }, 1500);
  }

  function handleVoiceTranscribed(event) {
      const text = event.detail;
      if (text) {
          animatedInput.value = text;
          sendMessage();
      }
  }

  onMounted(async () => {
      await nextTick();

      if (lottieHeader.value) {
          lottieHeaderInstance = lottie.loadAnimation({
              container: lottieHeader.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: lottieHeaderJson,
          });
      }

      const cached = await loadMessages();
      if (cached) {
          // Ensure timestamps exist for loaded messages
          messages.value = cached.map(msg => ({
              ...msg,
              timestamp: msg.timestamp || Date.now()
          }));
      }

      await nextTick();
      scrollToBottom();

      if (props.initialMessage) {
          animatedInput.value = props.initialMessage;
          sendMessage();
      }

      window.addEventListener('voice-transcribed', handleVoiceTranscribed);
  });

  onUnmounted(() => {
      window.removeEventListener('voice-transcribed', handleVoiceTranscribed);
      if (lottieInstance) lottieInstance.destroy();
      if (lottieHeaderInstance) lottieHeaderInstance.destroy();
  });
</script>

<style scoped>
  @import "@/ai/aiStyles/ChatBotComponent.css";
</style>
